import '../../css/EditOrganization.css'
import { useEffect, useState } from "react"
import { useLocation, useHistory } from 'react-router-dom'
import {
  OrganizationType,
  OrganizationUser,
  KeyBoolean,
  KeyString,
  UpdateUserRolesForOrganizationType,
  UserRoleToUpdateType,
} from "../../types"
import { ROUTE } from '../../routes'
import { useUserContext } from '../../context/UserContext';
import { AdminApi } from '../../api/admin';
import { useOrganizationContext } from '../../context/OrganizationContext';
import { toastNotifyError, toastNotifySuccess, toastNotifyInfo } from '../common/ToastMessage'
import { userHasPermission } from '../../helpers/permissionsHelper'
import { SimpleDialog } from '../common/SimpleDialog';
import { ToggleSlider } from '../common/ToggleSlider'
import { Table, Cell, Row } from '../common/TableComponents'
import { useCustomTranslation } from '../../helpers/CustomTranslationsComps'
import ErrorBoundary from '../../errorHandlers/ErrorBoundry'
import { BsJustify } from 'react-icons/bs'
import { Key } from 'readline'
import RichTextEditor from 'react-rte'
import { displayPartsToString } from 'typescript'



type RolesUpdated = {
  type: 'ROLES',
  value: string[]
}

type StatusChanged = {
  type: 'STATUS',
  value: string
}

type ShowContChanged = {
  type: 'SHOW_CONT'
  value: number
}

type updateOrganizationUserType = RolesUpdated | StatusChanged | ShowContChanged

const UserAdministrationComponent = () => {
  const location = useLocation()
  const history = useHistory()
  const userCtx = useUserContext()
  const orgCtx = useOrganizationContext()

  const [organization, setOrganization] = useState<OrganizationType | null>(null)
  const [openDialg, setOpenDialog] = useState<boolean>(false)
  const [users, setUsers] = useState<OrganizationUser[]>([])
  const [selectedUser, setSelectedUser] = useState<OrganizationUser | null>(null)
  const [selectedUserRoles, setSelectedUserRoles] = useState<Array<KeyBoolean>>([])
  const { getTranslation } = useCustomTranslation(orgCtx?.organization?.id || '')
  const [filterTxt, setFilterTxt] = useState<string>("")
  const [filteredUsers, setFilteredUsers] = useState<OrganizationUser[]>([])
  const [showRoles, setShowRoles] = useState<Boolean>(false)
  const [roles, setRoles] = useState<Array<String>>([])
  const [highlightedRoles, setHighlightedRoles] = useState<Array<string>>([])

  const handleRoleUpdate = (role: string, value: boolean) => {
    let updatedValues = JSON.parse(JSON.stringify(selectedUserRoles))
    updatedValues.forEach((r: KeyBoolean) => {
      let [entires] = Object.entries(r)
      if (entires[0] === role) {
        r[role] = value
      }
    });
    setSelectedUserRoles(updatedValues)
  }

  const getDialogContent = () => {
    return (
      <div className="organization-user-admin-role-status-container">
        {selectedUserRoles.map((r: KeyBoolean) => {
          const [entries] = Object.entries(r)
          if (entries[0] === 'USER') return null
          return (
            <div className="organization-user-admin-role-status-pair">
              <div>{entries[0]}</div>
              <div style={{
                justifyContent: 'center',
                display: 'flex'
              }}>
                <ToggleSlider
                  onText={""}
                  offText={""}
                  on={entries[1]}
                  updateState={() => handleRoleUpdate(entries[0], !entries[1])}
                  colorState
                />
              </div>
            </div>
          )
        })
        }
      </div >
    )
  }

  const formatRoles = (actionRoles: KeyString, user: OrganizationUser) => {
    let formattedRoles: Array<KeyBoolean> = []
    for (const [key, value] of Object.entries(actionRoles)) {
      let r = { [key]: user.roles.includes(value) }
      formattedRoles.push(r)
    }

    return formattedRoles
  }

  const prepareForUserRoleEdit = (user: OrganizationUser) => {
    if (!userCtx || !userCtx.actionRoles) return

    let roleStatsus = formatRoles(userCtx.actionRoles, user)
    setSelectedUserRoles(roleStatsus)
    setSelectedUser(user)
    setOpenDialog(true)
  }

  const updateOrganizationUser = (updates: updateOrganizationUserType, targetUser: string) => {
    let orgUsers = JSON.parse(JSON.stringify(users))
    if (updates.type === 'ROLES') {
      orgUsers.forEach((user: OrganizationUser) => {
        if (user.userId === targetUser) {
          user.roles = updates.value
        }
      });

      filteredUsers.forEach((user: OrganizationUser) => {
        if (user.userId === targetUser) {
          user.roles = updates.value
        }
      });

    } else if (updates.type === 'STATUS') {
      orgUsers.forEach((user: OrganizationUser) => {
        if (user.userId === targetUser) {
          user.status = updates.value
        }
      });

      filteredUsers.forEach((user: OrganizationUser) => {
        if (user.userId === targetUser) {
          user.status = updates.value
        }
      });
    } else {
      orgUsers.forEach((user: OrganizationUser) => {
        if (user.userId === targetUser) {
          switch(updates.value) {
            case 0:
              user.show_contributions = false
              break;
            case 1:
              user.show_contributions = true
              break;
          }
        }
      });

      filteredUsers.forEach((user: OrganizationUser) => {
        if (user.userId === targetUser) {
          switch(updates.value) {
            case 0:
              user.show_contributions = false
              break;
            case 1:
              user.show_contributions = true
              break;
          }
        }
      });
    }

    setUsers(orgUsers)
    setFilteredUsers(filteredUsers)
    setSelectedUser(null)
    setSelectedUserRoles([])

  }

  const verifyChanges = (actionRoles: KeyString, user: OrganizationUser) => {
    const originalRoles = formatRoles(actionRoles, user)
    if (JSON.stringify(selectedUserRoles) === JSON.stringify(originalRoles)) {
      toastNotifyInfo(getTranslation('ADMIN.USER_ADMINISTRATION.NO_CHANGES_TO_SAVE'))
      return false
    }
    return true
  }

  const handleUserRoleUpdate = async () => {
    setOpenDialog(false)
    if (!selectedUserRoles || !selectedUser || !userCtx || !userCtx.actionRoles || !organization) return
    if (!verifyChanges(userCtx.actionRoles, selectedUser)) return

    let data: UpdateUserRolesForOrganizationType = {
      userId: selectedUser.userId,
      roles: []
    }
    const targetUser = JSON.parse(JSON.stringify(selectedUser.userId))
    selectedUserRoles.forEach(r => {
      let [entires] = Object.entries(r)
      let roleToUpdate: UserRoleToUpdateType = {
        role: entires[0],
        attained: entires[1]
      }
      data.roles.push(roleToUpdate)
    })

    try {
      const response = await AdminApi.updateUserOrganizationRoles(organization.id, data)
      updateOrganizationUser({ type: 'ROLES', value: response.roles }, targetUser)
      toastNotifySuccess(getTranslation('GLOBAL.UPDATE_SUCCESS'))
    } catch (error: any) {
      console.error(error)
      let message = error.response ? error.response.data.message : getTranslation('GLOBAL.UNKNOW_ERROR')
      toastNotifyError(message)
    }
  }

  const handleSuspenUser = async (user: OrganizationUser) => {
    try {

      const response = await AdminApi.suspendUser(user.userId, organization!.id)
      updateOrganizationUser({ type: 'STATUS', value: response }, user.userId)

      toastNotifySuccess(getTranslation('GLOBAL.UPDATE_SUCCESS'))
    } catch (error: any) {
      let message = error.response ? error.response.data.message : getTranslation('GLOBAL.UNKNOW_ERROR')
      toastNotifyError(message)
    }
  }

  const handleReActivateUser = async (user: OrganizationUser) => {
    try {

      const response = await AdminApi.activateUser(user.userId, organization!.id)
      updateOrganizationUser({ type: 'STATUS', value: response.status }, user.userId)
      toastNotifySuccess(getTranslation('GLOBAL.UPDATE_SUCCESS'))
    } catch (error: any) {
      let message = error.response ? error.response.data.message : getTranslation('GLOBAL.UNKNOW_ERROR')
      toastNotifyError(message)
    }
  }


  const handleShowPicture = async (user: OrganizationUser) => {
    try {
      let showCont = 1
      if (user.show_contributions === true) {
        showCont = 0
      }

      const response = await AdminApi.showContribution(user.userId, organization!.id, showCont)
      updateOrganizationUser({ type: 'SHOW_CONT', value: response }, user.userId)
      toastNotifySuccess(getTranslation('GLOBAL.UPDATE_SUCCESS'))
    } catch (error: any) {
      let message = error.response ? error.response.data.message : getTranslation('GLOBAL.UNKNOW_ERROR')
      toastNotifyError(message)
    }
  }

  useEffect(() => {
    if (userCtx) {
      let actionRoles = userCtx.actionRoles

      let roleList: Array<String> = []
      for (const [key, value] of Object.entries(actionRoles)) {
        roleList.push(key)
      }

      setRoles(roleList)
    }
  }, [])

  useEffect(() => {

    const getUsers = async (organizationId: string) => {
      const response = await AdminApi.getOrgUsers(organizationId)
      setUsers(response)
      setFilteredUsers(response)
    }

    if (!userCtx || !userCtx.loggedInUser || !orgCtx || !orgCtx.organization) {
      history.push(ROUTE.HOME)
    } else {
      const orgId = orgCtx.organization.id
      const userRoles = userCtx.loggedInUser.roles

      if (!(userHasPermission(userRoles, userCtx.actionRoles.SUPER_ADMIN, orgId, userCtx.userIsPlaformAdmin) || userHasPermission(userRoles, userCtx.actionRoles.ADMIN, orgId, userCtx.userIsPlaformAdmin))) {
        history.push(ROUTE.HOME)
      }
      setOrganization(orgCtx.organization)
      if (!users.length) {
        getUsers(orgId)
      }
    }

  }, [location, history, userCtx, orgCtx, users.length])

  const disableActionButtons = userCtx && userCtx.loggedInUser && organization ? !userHasPermission(userCtx.loggedInUser.roles, userCtx.actionRoles.SUPER_ADMIN, organization.id, userCtx.userIsPlaformAdmin) : true
  const getStatusColor = (currentClasses: string, status: string, user: OrganizationUser) => {
    if (status === 'active') {
      return currentClasses + ' user-active'
    }

    if (status === 'suspended') {
      return currentClasses + ' user-suspended'
    }

    return currentClasses
  }

  const filterUsers = (filterStr: string) => {
    setFilterTxt(filterStr)
    
    if (filterStr.length > 0) {
      let userList: OrganizationUser[] = []

      if (highlightedRoles.length == 0) {
        userList = users
      } else {
        userList = filteredUsers
      }

      const filteredUserList = userList.filter(user => user.firstName.toLowerCase().includes(filterTxt.toLowerCase()) 
      || user.email.toLowerCase().includes(filterTxt.toLowerCase()) 
      || user.lastName.toLowerCase().includes(filterTxt.toLowerCase()))

      setFilteredUsers(filteredUserList)
    } else {
      setFilteredUsers(users)
    }    
  }

  const showRolesDropdown = () => {
    setShowRoles(!showRoles);
  }

  const setHighlightedRole = (role: String) => {
    let tmpHighlightedArr = highlightedRoles

    if (tmpHighlightedArr.includes(role.toString())) {
      let index = tmpHighlightedArr.indexOf(role.toString())

      if (index != -1) {
        tmpHighlightedArr.splice(index, 1)
      }
    } else {
      tmpHighlightedArr.push(role.toString())
    }

    setHighlightedRoles(tmpHighlightedArr)

    filterOnRoles()
  }

  useEffect(() => {
    if (highlightedRoles.length != 0) {
      filterOnRoles()
    }
  }, [filterTxt])

  const filterOnRoles = () => {
    let userList: OrganizationUser[] = []

    if (filterTxt.length == 0) {
      userList = users
    } else {
      userList = filteredUsers
    }

    console.log(userList, filterTxt)

    if (highlightedRoles.length > 0) {
      let filteredUsersForRoles: OrganizationUser[] = [] 

      highlightedRoles.forEach(role => {
        userList.forEach(user => {
          
          if (user.roles.includes(role) && (filteredUsersForRoles.includes(user) != true)) {
            filteredUsersForRoles.push(user)
          }
        })
      })
    
      setFilteredUsers(filteredUsersForRoles)
    } else {
      setFilteredUsers(users)

      if (filterTxt.length != 0) {
        filterUsers(filterTxt)
      } 
    }
  }

  return (
    <div className="App">
      <div className="app-content user-administration-page">
        {!organization || !users.length ? <div className="loader--loading"></div> : null}
        <h1>{organization?.name}</h1>
        <h2>{getTranslation('ADMIN.USER_ADMINISTRATION.HEADING')}</h2>

        
        <div className='dropdown-menu' >
          <div className='filter-user-box'>

            <input type="text" placeholder='Search user...' onChange={e => filterUsers(e.target.value)} value={filterTxt}></input>

          </div>
          
          <div
              className="filter-roles"
              onClick={() => showRolesDropdown()}
            > 
            filter roles {showRoles ? '▲' : '▼'}
                
          
          { showRoles == true ?
            <div
              className="dropDown-filter-roles"
            >
              {roles.map((role: String) => {
                return (
                  <div 
                    className="roles"
                    onClick={() => setHighlightedRole(role)}
                    style={{backgroundColor: highlightedRoles.includes(role.toString()) ? '#292922': 'none'}}
                  > 
                    {role}
                  </div>
                )
              })}  
            </div>: ""}  
          </div>    

        </div>
        <div className="organization-user-admin-container">
          <div className="organization-user-admin-container-header">
            <h3>{getTranslation('ADMIN.USER_ADMINISTRATION.TABLE_HEADING')}</h3>
          </div>
          <Table>
            <Row>
              <Cell className='Email'>{getTranslation('REGISTRATION.EMAIL')}</Cell>
              <Cell className='FirstName'>{getTranslation('REGISTRATION.FIRST_NAME')}</Cell>
              <Cell className='LastName'>{getTranslation('REGISTRATION.LAST_NAME')}</Cell>
              <Cell className="Role">{getTranslation('ADMIN.USER_ADMINISTRATION.TABLE_COLUMNS.ROLES')}</Cell>
              <Cell className='Status'>{getTranslation('ADMIN.USER_ADMINISTRATION.TABLE_COLUMNS.STATUS')}</Cell>
              <Cell className="CRUD">{getTranslation('ADMIN.USER_ADMINISTRATION.TABLE_COLUMNS.ACTIONS')}</Cell>
              <Cell className='Contribution'>{getTranslation('ADMIN.USER_ADMINISTRATION.TABLE_COLUMNS.CONTRIBUTIONS')}</Cell>
            </Row>
            {filteredUsers?.map(user => {
              return (
                <Row>
                  <Cell className='Email'>
                    <div
                      style={{ cursor: 'pointer' }}
                      onClick={() => history.push(`/organization/${organization?.id}/profile/${user.userId}`)}>
                      {user.email}
                    </div>
                  </Cell>
                  <Cell className='FirstName'>
                    {user.firstName}
                  </Cell>
                  <Cell className="LastName">{user.lastName}</Cell>
                  <Cell className="Role">{user.roles.map(role => {
                    if (role === 'USER') return null
                    return (
                      <div className="role-pill">
                        {role}
                      </div>
                    )
                  })}</Cell>
                  <Cell className='Status'><div className={getStatusColor('status-pill', user.status, user)}>{user.status}</div></Cell>
                  <Cell className="CRUD">
                    <button
                      className="btn-edit"
                      style={{ backgroundColor: orgCtx?.organization?.color, padding: '10px 20px', margin: '5px'}}
                      onClick={() => prepareForUserRoleEdit(user)}
                      disabled={disableActionButtons}
                    >
                      {getTranslation('ADMIN.USER_ADMINISTRATION.TABLE_ACTION_EDIT')}
                    </button>
                    
                    {user.status === 'active' ?
                      (<button
                        className="btn-danger"
                        onClick={() => {
                          handleSuspenUser(user)
                        }}
                        disabled={disableActionButtons}
                      >
                        {getTranslation('ADMIN.USER_ADMINISTRATION.TABLE_ACTION_SUSPEND')}
                      </button>) : (
                        <><button
                          className="btn-danger"
                          onClick={() => {
                            handleReActivateUser(user)
                          } }
                          disabled={disableActionButtons}
                        >
                          {getTranslation('ADMIN.USER_ADMINISTRATION.TABLE_ACTION_ACTIVATE')}
                        </button>
                        
                        <div
                          className="checkbox-show-picture"
                        >
                          <input 
                            type="checkbox" 
                            checked={user.show_contributions}
                            onClick={() => {
                              handleShowPicture(user)
                            }}
                          />
                          Show contribution
                        </div></>
                      )
                      

                    }
                  </Cell>
                  
                  <Cell className='Contribution'>{user.contributions}</Cell>
                </Row>
              )
            })}
          </Table>
        </div>
        <SimpleDialog
          open={openDialg}
          title={getTranslation('ADMIN.USER_ADMINISTRATION.EDIT_ROLES_DIALOG_TITLE', { userName: selectedUser?.firstName + ' ' + selectedUser?.lastName })}
          dialogContent={getDialogContent()}
          closeText={getTranslation('ADMIN.USER_ADMINISTRATION.EDIT_ROLES_DIALOG_CLOSE')}
          buttonActionText={getTranslation('ADMIN.USER_ADMINISTRATION.EDIT_ROLES_DIALOG_SAVE_ROLES')}
          cancelFunction={() => setOpenDialog(false)}
          actionFunction={handleUserRoleUpdate}
          organizationColor={orgCtx?.organization?.color}
          onClickOutSide={() => setOpenDialog(false)}
        />

      </div>
    </div >
  )
}

export const UserAdministration = ({ ...props }) => {
  const history = useHistory()
  if (props.environment === "prod") {
    return (
      <ErrorBoundary location="userAdministration" router={history}>
        <UserAdministrationComponent {...props} />
      </ErrorBoundary>
    )
  }
  return (
    <UserAdministrationComponent {...props} />
  )
}